import { computed, ComputedRef } from 'vue'

import { useEventSettings } from '@desktop/views/Relation/composables'
import { IncidentPlayerOption } from '@desktop/views/Relation/types'

import { useIncidentPlayers } from './useIncidentPlayers'

/**
 * Returns the first and second team players options for incidents.
 *
 * @returns {Object} An object containing first and second team players options.
 * @property {ComputedRef<IncidentPlayerOption[]>} firstTeamPlayersOptions - Computed reference to the first team players options.
 * @property {ComputedRef<IncidentPlayerOption[]>} secondTeamPlayersOptions - Computed reference to the second team players options.
 */
export function useInvertedIncidentPlayers(): {
  firstTeamPlayersOptions: ComputedRef<IncidentPlayerOption[]>
  secondTeamPlayersOptions: ComputedRef<IncidentPlayerOption[]>
} {
  const { eventSettings } = useEventSettings()

  const invertedParticipants = computed(
    () => eventSettings.value.invertedParticipants ?? false,
  )

  // composables
  const { homePlayersOptions, awayPlayersOptions } = useIncidentPlayers()

  // computed
  const firstTeamPlayersOptions = computed(() =>
    invertedParticipants.value
      ? awayPlayersOptions.value
      : homePlayersOptions.value,
  )

  const secondTeamPlayersOptions = computed(() =>
    invertedParticipants.value
      ? homePlayersOptions.value
      : awayPlayersOptions.value,
  )

  return {
    firstTeamPlayersOptions,
    secondTeamPlayersOptions,
  }
}
